import type { MechanicDefinition } from "./types";

export const regionMovement: MechanicDefinition = {
  id: "region_movement",
  disabled: true,
  name: "Region Movement",
  description: "Abstract zone-based navigation with encounters per region (Talisman-style)",
  icon: "Map",
  category: "systems",
  aiHints: {
    auditPasses: 1,
    purpose: "Provides zone-based navigation — moving between named regions triggers travel events and encounters.",
    whenToUse: "Board-game-style adventures, Talisman-like exploration, or worlds with distinct regions.",
    synergies: ["encounter_tables", "time_passage", "world_map", "territory"],
    conflicts: [],
    prerequisites: ["Location entities representing regions"],
    tags: ["movement", "regions", "travel", "zones", "exploration"],
    complexity: 2,
    exampleConfig: { travel_speed: "normal", encounter_chance: 0.3 },
    stateVariables: ["current_region", "visited_regions"],
  },
  configSchema: [
    {
      key: "movement_style",
      label: "Movement Style",
      type: "select",
      options: [
        { value: "free", label: "Free — move to any connected region" },
        { value: "dice", label: "Dice — roll to determine movement range" },
        { value: "action_points", label: "AP — spend action points to move" },
      ],
      default: "free",
    },
    {
      key: "region_types",
      label: "Region Types",
      type: "text",
      default: "town,wilderness,dungeon,ruins,mountain,swamp,coast",
      description: "Comma-separated region categories for encounter tables",
    },
    {
      key: "encounter_on_move",
      label: "Encounter on Move",
      type: "boolean",
      default: true,
      description: "Trigger random encounters when entering a new region",
    },
    {
      key: "fog_of_war",
      label: "Fog of War",
      type: "boolean",
      default: false,
      description: "Hide unvisited regions until the player explores them",
    },
  ],
  systemPromptSection: (config) => {
    const style = config.movement_style || "free";
    const regionTypes = (config.region_types || "town,wilderness,dungeon,ruins,mountain,swamp,coast").split(",").map((s: string) => s.trim());
    const encounterOnMove = config.encounter_on_move !== false;
    const fog = config.fog_of_war || false;

    return `## REGION MOVEMENT (Hard Mechanic — Board Game Style)
- The world is divided into abstract regions/zones. Each region has a type: ${regionTypes.join(", ")}.
- Movement style: ${style === "dice" ? "Roll 1d6 to determine how many regions the player can traverse per turn." : style === "action_points" ? "Player has movement points (AP) to spend. Each region costs 1-3 AP to enter depending on terrain difficulty." : "Player can move freely to any directly connected region per turn."}
- Use the move_to_region tool to change the player's current region.
- Each region has: name, type, description, connections (adjacent regions), difficulty_level (1-10), and notable features.
${encounterOnMove ? "- IMPORTANT: When the player enters a new region, ALWAYS roll for a random encounter using encounter_roll tool. The encounter type depends on region type and difficulty." : ""}
${fog ? "- Fog of War: Only describe regions the player has visited. Use reveal_region tool when a new area is discovered." : ""}
- Track visited regions in state. Let the player ask "where can I go?" to list connected regions.
- Some regions may be locked until conditions are met (quest completion, items, reputation).
- Region-appropriate NPCs and items should appear based on region type.
- Towns are generally safe havens; wilderness and dungeons are dangerous.
- Use the describe_region tool to give rich environmental details when arriving.

### REGION ENCOUNTER TABLE GUIDELINES
For each region type, encounters should follow this distribution:
- Town: 60% social, 20% merchant, 10% quest hook, 10% event
- Wilderness: 40% combat, 25% exploration, 20% creature, 15% weather event
- Dungeon: 50% combat, 20% trap/hazard, 15% treasure, 15% puzzle
- Ruins: 35% combat, 30% exploration/lore, 20% trap, 15% treasure
- Mountain: 30% environmental hazard, 25% combat, 25% exploration, 20% resource
- Swamp: 40% combat (creatures), 25% environmental hazard, 20% lore, 15% resource
- Coast: 30% social, 25% exploration, 25% combat (pirates/creatures), 20% resource`;
  },
  widgets: [
    {
      id: "region_map",
      type: "sidebar_tab",
      label: "Regions",
      icon: "Map",
      component: "RegionMapWidget",
      dataBindings: {
        currentRegion: "variables.current_region",
        visitedRegions: "variables.visited_regions",
        regionConnections: "variables.region_connections",
      },
      visibility: "when_data",
      priority: 15,
    },
    {
      id: "region_status",
      type: "status_bar_item",
      label: "Region",
      icon: "MapPin",
      component: "RegionStatusWidget",
      dataBindings: {
        currentRegion: "variables.current_region",
        regionType: "variables.current_region_type",
      },
      visibility: "when_data",
      priority: 3,
    },
  ],
  initializeState: (config) => ({
    current_region: "",
    current_region_type: "",
    visited_regions: [],
    region_connections: {},
    movement_points: config.movement_style === "action_points" ? 3 : undefined,
  }),
  onEvent: (event) => {
    // React to combat completion in a region
    if (event.type === "combat_end" && event.data.region_cleared) {
      return {
        narrativeNotes: [`🗺️ Region encounter cleared. Area is now safe to explore further.`],
      };
    }
    return null;
  },
  tools: [
    {
      type: "function",
      function: {
        name: "move_to_region",
        description: "Move the player to a connected region. Updates current location and triggers arrival logic.",
        parameters: {
          type: "object",
          properties: {
            region_name: { type: "string", description: "Name of the region to move to" },
            region_type: { type: "string", description: "Type of region (town, wilderness, dungeon, etc.)" },
            difficulty: { type: "number", description: "Difficulty level 1-10 of this region" },
            connections: {
              type: "array",
              items: { type: "string" },
              description: "Names of regions connected to this one",
            },
            description: { type: "string", description: "Brief description of the region" },
            movement_cost: { type: "number", description: "AP cost if using action points (default 1)" },
          },
          required: ["region_name", "region_type"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "encounter_roll",
        description: "Roll for a random encounter based on region type and difficulty. Returns encounter type and details.",
        parameters: {
          type: "object",
          properties: {
            region_type: { type: "string", description: "Type of region for encounter table lookup" },
            difficulty: { type: "number", description: "Region difficulty (1-10) affecting encounter severity" },
            modifier: { type: "number", description: "Optional modifier to encounter roll" },
          },
          required: ["region_type"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "reveal_region",
        description: "Reveal a previously hidden region (fog of war). Add it to the known map.",
        parameters: {
          type: "object",
          properties: {
            region_name: { type: "string", description: "Name of the region being revealed" },
            region_type: { type: "string", description: "Type of region" },
            connections: {
              type: "array",
              items: { type: "string" },
              description: "Connected region names",
            },
            discovery_method: { type: "string", description: "How the region was discovered (exploration, NPC hint, map found, etc.)" },
          },
          required: ["region_name", "region_type"],
        },
      },
    },
  ],
};
